import { Helmet } from "react-helmet-async";
import { BreadcrumbsJsonLd } from "@/components/seo/BreadcrumbsJsonLd";
import { ArticleJsonLd } from "@/components/seo/ArticleJsonLd";
import { WeightLossGuideLink } from "@/components/seo/WeightLossGuideLink";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollFadeWrapper } from "@/components/ui/scroll-fade-wrapper";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { clinicalData } from "@/data/peptide-clinical-data";
import { APP_URL, canonicalUrl } from "@/lib/app-config";

/**
 * Gated therapy library. Renders the FULL clinical dataset — including
 * compounded peptides that are not surfaced publicly. Access is enforced
 * via ProtectedRoute at the route level in App.tsx.
 */
const TherapyLibrary = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Therapy Library | Elara Health Patient Portal</title>
        <meta
          name="description"
          content="The complete protocol library — reviewed and prescribed one-on-one by your licensed Elara Health clinician."
        />
        {/* Gated — don't index. */}
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={canonicalUrl("/therapies")} />
      </Helmet>
      <BreadcrumbsJsonLd items={[{ name: "Therapy Library" }]} />
      <ArticleJsonLd
        path="/therapies"
        headline="Elara Health Therapy Library: Clinical Protocol Reference"
        description="The complete protocol library — reviewed and prescribed one-on-one by your licensed Elara Health clinician."
        datePublished="2026-07-02"
        dateModified="2026-08-10"
        section="Clinical Protocols"
        isAccessibleForFree={false}
      />

      <Navbar />

      <div className="pt-24 pb-16">
        <section className="container mx-auto px-4 mb-10">
          <div className="max-w-5xl mx-auto">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
            >
              <ArrowLeft size={14} /> Back to dashboard
            </Link>

            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                <ShieldCheck size={20} className="text-primary" />
              </div>
              <div className="space-y-1">
                <Badge variant="secondary" className="text-[10px]">Patient portal · confidential</Badge>
                <h1 className="font-display text-4xl md:text-5xl text-foreground">
                  Therapy Library
                </h1>
              </div>
            </div>
            <p className="text-lg text-muted-foreground max-w-3xl">
              The full catalog of protocols your Elara Health clinician may evaluate for you.
              Availability and appropriateness are determined during clinical review — this page
              is for informational reference only and does not constitute a prescription.
            </p>
            <p className="text-sm text-muted-foreground max-w-3xl mt-3">
              Focused on metabolic health? Read our <WeightLossGuideLink /> for dosing, expected
              timelines, and side-effect management.
            </p>
          </div>
        </section>

        <section className="container mx-auto px-4 mb-16">
          <Tabs defaultValue="weight" className="max-w-5xl mx-auto">
            <ScrollFadeWrapper fadeColor="from-background">
              <TabsList className="flex overflow-x-auto no-scrollbar h-auto gap-1 bg-secondary p-1.5 w-full justify-start">
                {clinicalData.map((cat) => (
                  <TabsTrigger key={cat.id} value={cat.id} className="gap-1.5 text-xs shrink-0">
                    <cat.icon size={14} /> {cat.patientTitle}
                  </TabsTrigger>
                ))}
              </TabsList>
            </ScrollFadeWrapper>

            {clinicalData.map((cat) => (
              <TabsContent key={cat.id} value={cat.id} className="mt-6">
                <div className="mb-6">
                  <h2 className="font-display text-3xl text-foreground mb-2">{cat.title}</h2>
                  <p className="text-muted-foreground">{cat.description}</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {cat.peptides.map((p) => (
                    <Card key={p.name} className="border-border bg-card hover:shadow-md transition-shadow">
                      <CardContent className="p-5 space-y-3">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-display text-lg text-foreground leading-tight">{p.name}</h3>
                          {p.publicAllowed && (
                            <Badge variant="secondary" className="text-[10px] shrink-0">
                              FDA / public
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{p.simple}</p>
                        <div className="pt-2 border-t border-border space-y-1.5">
                          <p className="text-[11px] uppercase tracking-widest text-muted-foreground/70">
                            Mechanism
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{p.mechanism}</p>
                          <p className="text-[11px] uppercase tracking-widest text-muted-foreground/70 pt-1">
                            Indications
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed">{p.indications}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </section>

        <section className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="border-border bg-secondary/40">
              <CardContent className="p-6 space-y-3">
                <h3 className="font-display text-lg text-foreground">Important notice</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Only a subset of therapies listed here are FDA-approved for specific indications.
                  Others are compounded medications prepared by U.S.-licensed 503A pharmacies for
                  individual patient needs, based on clinician determination. Listing on this page
                  is not an endorsement, prescription, or offer to sell. Your provider will
                  determine appropriateness during your consultation, and not all patients will
                  qualify. Individual results vary.
                </p>
                <div className="pt-2">
                  <Link to="/book">
                    <Button size="sm">Book a consultation</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default TherapyLibrary;
